import { z } from "zod";

// Minimal GeoJSON (RFC 7946) schemas. Coordinates are always [lon, lat] in WGS84.

export const Latitude = z.number().min(-90).max(90);
export const Longitude = z.number().min(-180).max(180);

export const LonLat = z.tuple([Longitude, Latitude]);
export type LonLat = z.infer<typeof LonLat>;

/** A closed ring: at least 4 positions, first equals last (checked by PostGIS on write). */
const LinearRing = z.array(LonLat).min(4).max(5000);

export const PointGeometry = z.object({
  type: z.literal("Point"),
  coordinates: LonLat,
});
export type PointGeometry = z.infer<typeof PointGeometry>;

export const PolygonGeometry = z.object({
  type: z.literal("Polygon"),
  coordinates: z.array(LinearRing).min(1).max(50),
});
export type PolygonGeometry = z.infer<typeof PolygonGeometry>;

export const MultiPolygonGeometry = z.object({
  type: z.literal("MultiPolygon"),
  coordinates: z.array(z.array(LinearRing).min(1).max(50)).min(1).max(50),
});
export type MultiPolygonGeometry = z.infer<typeof MultiPolygonGeometry>;

/** Input for areas drawn in the panel; stored as MultiPolygon after ST_MakeValid. */
export const AreaGeometry = z.discriminatedUnion("type", [PolygonGeometry, MultiPolygonGeometry]);
export type AreaGeometry = z.infer<typeof AreaGeometry>;

export const LineStringGeometry = z.object({
  type: z.literal("LineString"),
  coordinates: z.array(z.array(z.number()).min(2).max(4)).min(2),
});
export type LineStringGeometry = z.infer<typeof LineStringGeometry>;

export const MultiLineStringGeometry = z.object({
  type: z.literal("MultiLineString"),
  coordinates: z.array(z.array(z.array(z.number()).min(2).max(4)).min(2)),
});
export type MultiLineStringGeometry = z.infer<typeof MultiLineStringGeometry>;

/** [west, south, east, north] */
export const BBox = z.tuple([Longitude, Latitude, Longitude, Latitude]);
export type BBox = z.infer<typeof BBox>;
